import React, { useEffect, useRef, useState } from "react";
import { useAnagramsContext } from "../providers/anagram-provider";

import "./letter-picker.scss";
import { Letter } from "./letter";

type SelectedLetter = {
  idx: number;
  letter: string;
  order: number;
};

function LetterPicker() {
  const anagramsContext = useAnagramsContext();

  const { letters } = anagramsContext;

  const [selectedLetters, setSelectedLetters] = useState<SelectedLetter[]>([]);
  const [isMouseDown, setIsMouseDown] = useState(false);

  function endSelection() {
    if (selectedLetters.length > 0) {
      anagramsContext.checkWord(
        selectedLetters.map((selected) => selected.letter).join("")
      );
    }
    setSelectedLetters([]);
    setIsMouseDown(false);
  }

  // The document-level release listeners must call the latest endSelection,
  // otherwise they would see the selection as it was when the press started
  // (empty) and silently drop the word.
  const endSelectionRef = useRef(endSelection);
  endSelectionRef.current = endSelection;

  useEffect(() => {
    if (!isMouseDown) {
      return;
    }
    const release = () => endSelectionRef.current();
    document.addEventListener("mouseup", release);
    document.addEventListener("mouseleave", release);
    return () => {
      document.removeEventListener("mouseup", release);
      document.removeEventListener("mouseleave", release);
    };
  }, [isMouseDown]);

  function onMouseDown(
    _evt: React.MouseEvent | React.TouchEvent,
    letter: string | null = null,
    idx: number | null = null
  ) {
    setIsMouseDown(true);
    if (letter !== null && idx !== null) {
      setSelectedLetters([{ idx, letter, order: 0 }]);
    }
  }

  function onMouseEnter(_evt: unknown, letter: string, idx: number): void {
    // if it has been selected, don't select it again
    if (!isMouseDown || selectedLetters.some((s) => s.idx === idx)) {
      return;
    }

    setSelectedLetters([
      ...selectedLetters,
      { idx, letter, order: selectedLetters.length },
    ]);
  }

  // The letter tile under a touch point, if there is one.
  function letterAt(
    evt: React.TouchEvent
  ): { letter: string; idx: number } | null {
    const touch = evt.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!el?.classList.contains("letter")) {
      return null;
    }
    return {
      letter: el.getAttribute("data-letter") ?? "",
      idx: parseInt(el.getAttribute("data-idx") ?? "-1"),
    };
  }

  function touchStartEmulator(evt: React.TouchEvent) {
    const hit = letterAt(evt);
    if (hit) {
      onMouseDown(evt, hit.letter, hit.idx);
      return;
    }

    const touch = evt.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (el?.classList.contains("hold-all-the-things")) {
      setIsMouseDown(true);
    }
  }

  function touchMoveEmulator(evt: React.TouchEvent) {
    const hit = letterAt(evt);
    if (hit) {
      onMouseEnter(evt, hit.letter, hit.idx);
    }
  }

  return (
    <>
      {letters.length > 0 ? (
        <div
          className="hold-all-the-things"
          onTouchStart={touchStartEmulator}
          onTouchMove={touchMoveEmulator}
          onTouchEnd={endSelection}
        >
          <div className="shuffle-the-letters">
            <div
              onClick={(evt) => {
                evt.preventDefault();
                anagramsContext.shuffleTheLetters();
              }}
            >
              &#9852;
            </div>
          </div>
          <div
            className={"circle-holder " + getLetterCountClass(letters.length)}
            onMouseDown={onMouseDown}
            onMouseUp={endSelection}
          >
            {getLetterList(
              letters.join(""),
              selectedLetters,
              onMouseEnter,
              onMouseDown,
              endSelection
            )}
          </div>
          <div className="selectedLetters">
            {selectedLetters.map((l) => l.letter.toUpperCase()).join("")}
          </div>
        </div>
      ) : (
        <div>Oofda</div>
      )}
    </>
  );
}

function getLetterList(
  letters: string,
  selectedLetters: SelectedLetter[],
  onMouseEnter: (evt: unknown, letter: string, idx: number) => void,
  onMouseDown: (evt: React.MouseEvent, letter?: string, idx?: number) => void,
  onMouseUp: () => void
) {
  return letters.split("").map((letter, idx) => (
    <Letter
      letter={letter}
      key={idx}
      idx={idx}
      isSelected={selectedLetters.some((selected) => selected.idx === idx)}
      onMouseEnter={onMouseEnter}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
    />
  ));
}

function getLetterCountClass(lettersLength: number) {
  switch (lettersLength) {
    case 3: // short anagrams use the smallest layout
    case 4:
      return "four";
    case 5:
      return "five";
    case 6:
      return "six";
    case 7:
      return "seven";
    case 8:
      return "eight";
    case 9:
      return "nine";
    case 10:
      return "ten";
    case 11:
      return "eleven";
    case 12:
      return "twelve";
    case 13:
      return "thirteen";
    case 14:
      return "fourteen";
    case 15:
      return "fifteen";
    default:
      console.log(
        "really, we have a problem with the length of the string",
        lettersLength
      );
      return "four";
  }
}

export default LetterPicker;
